import React, { useState } from 'react'
import styled from 'styled-components'

// @ts-expect-error: no types in this version of @gnomad/ui
import { ExternalLink, List, ListItem, Modal, TextButton } from '@gnomad/ui'

import datasetConfig from '../../datasetConfig'
import { IndividualGeneAPIResponse } from '../GeneResultsPage/geneResultTableColumns'
import getDatasetBrowserUrl from '../datasetBrowserUrl'
import { DatasetId } from '../Browser'

interface GeneReferencesProps {
  gene: IndividualGeneAPIResponse
}

const GeneReferences = ({ gene }: GeneReferencesProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const gnomadDataset = gene.reference_genome === 'GRCh37' ? 'gnomad_r2_1' : 'gnomad_r4'
  const gnomadGeneUrl = `https://gnomad.broadinstitute.org/gene/${gene.gene_id}?dataset=${gnomadDataset}`

  const ensemblGeneUrl = `https://${
    gene.reference_genome === 'GRCh37' ? 'grch37.' : ''
  }ensembl.org/Homo_sapiens/Gene/Summary?g=${gene.gene_id}`

  const ucscReferenceGenomeId = gene.reference_genome === 'GRCh37' ? 'hg19' : 'hg38'
  const ucscUrl = `https://genome.ucsc.edu/cgi-bin/hgTracks?db=${ucscReferenceGenomeId}&position=chr${gene.chrom}%3A${gene.start}-${gene.stop}`

  return (
    <>
      <ExternalLink href={gnomadGeneUrl}>gnomAD</ExternalLink>,{' '}
      <ExternalLink href={ensemblGeneUrl}>Ensembl</ExternalLink>,{' '}
      <ExternalLink href={ucscUrl}>UCSC Browser</ExternalLink>,{' '}
      <TextButton
        onClick={() => {
          setIsExpanded(true)
        }}
      >
        and more
      </TextButton>
      {isExpanded && (
        <Modal
          initialFocusOnButton={false}
          onRequestClose={() => {
            setIsExpanded(false)
          }}
          title={`Gene resources for ${gene.symbol}`}
        >
          <List>
            <ListItem>
              <ExternalLink href={gnomadGeneUrl}>gnomAD</ExternalLink>
            </ListItem>
            <ListItem>
              <ExternalLink href={ensemblGeneUrl}>Ensembl</ExternalLink>
            </ListItem>
            <ListItem>
              <ExternalLink href={ucscUrl}>UCSC Browser</ExternalLink>
            </ListItem>
            <ListItem>
              <ExternalLink
                href={`https://www.genecards.org/cgi-bin/carddisp.pl?gene=${gene.symbol}`}
              >
                GeneCards
              </ExternalLink>
            </ListItem>
            {gene.omim_id && (
              <ListItem>
                <ExternalLink href={`https://omim.org/entry/${gene.omim_id}`}>OMIM</ExternalLink>
              </ListItem>
            )}
            <ListItem>
              <ExternalLink
                href={`https://decipher.sanger.ac.uk/gene/${gene.gene_id}#overview/protein-info`}
              >
                DECIPHER
              </ExternalLink>
            </ListItem>
            {gene.hgnc_id && (
              <ListItem>
                <ExternalLink href={`https://search.clinicalgenome.org/kb/genes/${gene.hgnc_id}`}>
                  ClinGen
                </ExternalLink>
              </ListItem>
            )}
            {gene.hgnc_id && (
              <ListItem>
                <ExternalLink
                  href={`https://www.genenames.org/data/gene-symbol-report/#!/hgnc_id/${gene.hgnc_id}`}
                >
                  HGNC
                </ExternalLink>
              </ListItem>
            )}
          </List>
        </Modal>
      )}
    </>
  )
}

const DescriptionList = styled.dl`
  dt {
    width: 115px;
    font-weight: bold;
  }

  dd {
    margin-left: 1em;
  }
`

const DescriptionListItem = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 0.5em;
`

const SymbolArrayAsDescriptionListItem = ({
  label,
  symbolArray,
}: {
  label: string
  symbolArray: string[]
}) => {
  return (
    <DescriptionListItem>
      <dt>{label} symbols</dt>
      <dd>
        {symbolArray
          .map((symbol) => <>{symbol}</>)
          .reduce((acc: React.ReactNode[], item) => [...acc, ', ', item], [])
          .slice(1)}
      </dd>
    </DescriptionListItem>
  )
}

const AlternateGeneSymbolRows = ({ gene }: { gene: IndividualGeneAPIResponse }) => {
  return (
    <>
      {gene.previous_symbols && (
        <SymbolArrayAsDescriptionListItem label={'Prior'} symbolArray={gene.previous_symbols} />
      )}
      {gene.alias_symbols && (
        <SymbolArrayAsDescriptionListItem label={'Alias'} symbolArray={gene.alias_symbols} />
      )}
    </>
  )
}

interface GeneInfoProps {
  gene: IndividualGeneAPIResponse
  showManeSelectTranscript?: boolean
}
const GeneInfo = ({ gene, showManeSelectTranscript = false }: GeneInfoProps) => {
  const ucscReferenceGenomeId = gene.reference_genome === 'GRCh38' ? 'hg38' : 'hg19'

  const allOtherStudies: { id: DatasetId }[] = [
    { id: 'AGB' },
    { id: 'BipEx' },
    { id: 'Epi25' },
    { id: 'GP2' },
    { id: 'IBD' },
    { id: 'SCHEMA' },
  ]
  const otherStudies = allOtherStudies.filter(({ id }) => id !== datasetConfig.datasetId)

  const datasetsToDisplayAliasSymbols: DatasetId[] = ['BipEx', 'SCHEMA']

  return (
    <DescriptionList>
      {datasetsToDisplayAliasSymbols.indexOf(datasetConfig.datasetId) !== -1 && (
        <AlternateGeneSymbolRows gene={gene} />
      )}

      <DescriptionListItem>
        <dt>Genome build</dt>
        <dd>
          {gene.reference_genome} / {ucscReferenceGenomeId}
        </dd>
      </DescriptionListItem>
      <DescriptionListItem>
        <dt>Ensembl gene ID</dt>
        <dd>{gene.gene_id}</dd>
      </DescriptionListItem>
      {gene.canonical_transcript_id && (
        <DescriptionListItem>
          <dt>Ensembl transcript ID</dt>
          <dd>{gene.canonical_transcript_id}</dd>
        </DescriptionListItem>
      )}
      {showManeSelectTranscript && (
        <DescriptionListItem>
          <dt>MANE Select transcript</dt>
          <dd>{gene.mane_select_transcript_id ?? '-'}</dd>
        </DescriptionListItem>
      )}
      <DescriptionListItem>
        <dt>Region</dt>
        <dd>{`Chr${gene.chrom}:${gene.start}-${gene.stop}`}</dd>
      </DescriptionListItem>
      <DescriptionListItem>
        <dt>Gene resources</dt>
        <dd>
          <GeneReferences gene={gene} />
        </dd>
      </DescriptionListItem>
      <DescriptionListItem>
        <dt>Other studies</dt>
        <dd>
          {otherStudies
            .map(({ id }) => {
              return (
                <ExternalLink key={id} href={getDatasetBrowserUrl(id, `/gene/${gene.gene_id}`)}>
                  {id}
                </ExternalLink>
              )
            })
            .reduce((acc: React.ReactNode[], link) => [...acc, ', ', link], [])
            .slice(1)}
        </dd>
      </DescriptionListItem>
    </DescriptionList>
  )
}

export default GeneInfo
